import { colors } from "@indeed/ifl-tokens";
import React, { type CSSProperties, useEffect, useMemo, useRef } from "react";

import type { HtmlRendererProps } from "./HtmlContentWebview";

const HTML_CONTENT_ID = "react-native-html-content";
const HTML_CONTENT_CLASS = "react-native-html-content";

// Selectors target both `#react-native-html-content` (legacy default) and
// `.react-native-html-content` (used when callers opt in by passing any of
// `injectedStyles`, `testID`, `backgroundColor`, or `textColor`). The class
// path lets multiple instances coexist on the same page without producing
// duplicate DOM ids.
const htmlContentStyles = `
    <style>
        @layer htmlContent {
            /*
            * This is added in order to break the margin collapse from the first and last elements so that the bounding client rectanble's height
            * includes the margin of the first and last elements.
            */
            #${HTML_CONTENT_ID},
            .${HTML_CONTENT_CLASS} {
                padding-top: 1px;
                padding-bottom: 1px;

                font-size: 1rem;
                line-height: 24px;
                font-family: "IndeedSans", "Indeed Sans", "Noto Sans", "Helvetica Neue", "Helvetica", "Arial", "Liberation Sans", "Roboto", "Noto", sans-serif;
                color: ${colors.foreground["[base]"]};
                margin: 0;
                word-wrap: break-word;
                overflow: hidden;
            }

            #${HTML_CONTENT_ID} p,
            .${HTML_CONTENT_CLASS} p {
                margin-top: 8px;
                margin-bottom: 8px;
            }

            #${HTML_CONTENT_ID} h1,
            .${HTML_CONTENT_CLASS} h1 {
                font-size: 1.5rem;
            }

            #${HTML_CONTENT_ID} h1,
            #${HTML_CONTENT_ID} h2,
            #${HTML_CONTENT_ID} h3,
            #${HTML_CONTENT_ID} h4,
            #${HTML_CONTENT_ID} h5,
            #${HTML_CONTENT_ID} h6,
            .${HTML_CONTENT_CLASS} h1,
            .${HTML_CONTENT_CLASS} h2,
            .${HTML_CONTENT_CLASS} h3,
            .${HTML_CONTENT_CLASS} h4,
            .${HTML_CONTENT_CLASS} h5,
            .${HTML_CONTENT_CLASS} h6 {
                margin-top: 24px;
                margin-bottom: 16px;
                color: ${colors.neutral["800"]};
            }

            #${HTML_CONTENT_ID} ul,
            #${HTML_CONTENT_ID} ol,
            .${HTML_CONTENT_CLASS} ul,
            .${HTML_CONTENT_CLASS} ol {
                padding-left: 24px;
                margin-bottom: 16px;
            }

            #${HTML_CONTENT_ID} li,
            .${HTML_CONTENT_CLASS} li {
                margin-bottom: 8px;
                padding-left: 8px;
            }
        }
    </style>
`;

export function HtmlContent(props: HtmlRendererProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Focus the first element when content is revealed
  useEffect(() => {
    if (props.shouldFocusContent && contentRef.current) {
      // Get the first child element
      const firstElement = contentRef.current.firstElementChild;

      if (firstElement && firstElement instanceof HTMLElement) {
        firstElement.setAttribute("tabindex", "-1");
        firstElement.style.outline = "none";
        firstElement.focus();
      } else {
        // If no first element, focus the content div itself
        contentRef.current.setAttribute("tabindex", "-1");
        contentRef.current.style.outline = "none";
        contentRef.current.focus();
      }
    }
  }, [props.shouldFocusContent]);

  const inlineStyle = useMemo<CSSProperties | undefined>(() => {
    if (!props.backgroundColor && !props.textColor) {
      return undefined;
    }
    return {
      ...(props.backgroundColor ? { backgroundColor: props.backgroundColor } : {}),
      ...(props.textColor ? { color: props.textColor } : {}),
    };
  }, [props.backgroundColor, props.textColor]);

  const innerHTML = useMemo(() => {
    const overrideStyles = props.injectedStyles ? `<style>${props.injectedStyles}</style>` : "";
    return `${htmlContentStyles}${overrideStyles}${props.children}`;
  }, [props.children, props.injectedStyles]);

  // Opt in to class-based scoping when callers pass any prop that would
  // collide on duplicate ids (e.g. multiple instances on the same page) or
  // that needs a unique hook (`testID`, `className`). Otherwise stay on the
  // legacy id for backwards compatibility with existing callers.
  const scopedByClass = Boolean(
    props.injectedStyles ||
    props.testID ||
    props.backgroundColor ||
    props.textColor ||
    props.className,
  );

  const className = scopedByClass
    ? [HTML_CONTENT_CLASS, props.className].filter(Boolean).join(" ")
    : undefined;

  return (
    <div
      ref={contentRef}
      id={scopedByClass ? undefined : HTML_CONTENT_ID}
      className={className}
      data-testid={props.testID}
      style={inlineStyle}
      dangerouslySetInnerHTML={{ __html: innerHTML }}
    />
  );
}
